import type {StepStatusType} from '@chakra-ui/react';
import type {JSX} from 'react';
import type {StoreApi} from 'zustand';

export type StepDef<TContext extends unknown, Tkey extends string = string> = {
  guard?: (context: TContext) => boolean;
  onActivate?: (context: Omit<StoreApi<TContext>, 'subscribe' | 'persist' | 'destroy'>) => void;
  onDeactivate?: (context: Omit<StoreApi<TContext>, 'subscribe' | 'persist' | 'destroy'>) => void;
  Sidebar: () => JSX.Element;
  Panel: () => JSX.Element;
  key: Tkey;
};

export type StepState<TContext extends unknown, Tkey extends string = string> = {
  index: number;
  status: StepStatusType;
} & StepDef<TContext, Tkey>;

export type StepsArray<TContext extends unknown, Tkey extends string = string> = [
  null,
  ...Array<StepState<TContext, Tkey>>,
];

export const createStepsHelper = <TContext extends unknown, Tkey extends string = string>() => {
  return {
    step: (step: StepDef<TContext, Tkey>) =>
      ({
        ...step,
      }) satisfies StepDef<TContext, Tkey>,
  };
};

export const createStepsState = <TContext, TKey extends string = string>(
  steps: Array<StepDef<TContext, TKey>>,
): StepsArray<TContext, TKey> => {
  const state: Array<StepState<TContext, TKey>> = steps.map((step, index) => ({
    ...step,
    index,
    status: `incomplete`,
    guard: step.guard ?? (() => true),
  }));

  return [null, ...state];
};
